<template>
  <ev-nav-dialog
    v-model="visible"
    :tabs="tabs"
    :current-tab.sync="tab"
    header="My Profile"
    @close="onClose"
  >
    <template v-slot:tabBody>
      <div v-if="tab === 0">
        <my-profile-header
          v-if="user"
          :user="user"
          @update-profile-picture="updateProfilePicture"
        />
        <user-info
          :edit="true"
          :user="user"
          :user-guid="userGuid"
          :company-guid="companyGuid"
          @change="handleUserInfoChange"
          @save="handleUserInfoSave"
        />
      </div>
      <div v-else-if="tab === 1">
        <permissions
          ref="permissions"
          :user="user"
          :view-only="true"
        />
      </div>
      <div v-else-if="tab === 2 && isEditingSelf">
        <change-password
          :key="passwordKey"
          :initiator-user-guid="initiatorUserGuid"
          @change="handlePasswordChange"
          @save="handlePasswordSave"
        />
      </div>
      <discard-changes-modal
        :toggle-modal="toggleDiscardChangesModalVisible"
        @close="handleConfirmDiscardChanges"
        @cancel="toggleDiscardChangesModal"
      />
    </template>
  </ev-nav-dialog>
</template>
<script>

import userInfo from './userInfo'
import Permissions from '../permissions'
import changePassword from './changePassword.vue'
import myProfileHeader from './myProfileHeader'
import discardChangesModal from './discardChangesModal'
import { user, updateUserProfilePicture } from '../graphql/users/users_queries.gql'
import { rolesPermissions } from '../graphql/companies_queries.gql'
import { mapGetters, mapMutations } from 'vuex'
import { isEmpty } from 'lodash'
import EvNavDialog from '../evui/evNavDialog.vue'

export default {
  name: 'MyProfile',

  components: {
    EvNavDialog,
    userInfo,
    changePassword,
    Permissions,
    myProfileHeader,
    discardChangesModal
  },
  props: {
    userGuid: {
      type: String,
      default: '',
      required: true
    },
    companyGuid: {
      type: String,
      required: false,
      default: null
    },
    value: {
      type: Boolean,
      default: false,
      required: false
    },
    initialTab: {
      type: Number,
      default: 0,
      required: false
    }
  },

  data () {
    return {
      visible: this.value,
      tab: this.initialTab,
      user: null,
      lastLoginAt: null,
      profilePictureUrl: '',
      roleEnvironments: {},
      hasUserInfoChanges: false,
      hasPasswordChanges: false,
      toggleDiscardChangesModalVisible: false,
      passwordKey: 100
    }
  },

  computed: {
    ...mapGetters({
      loggedInUser: 'user'
    }),
    width () {
      return this.$vuetify.breakpoint.lgAndUp ? '70%' : '80%'
    },
    hasChanges () {
      return this.hasUserInfoChanges || this.hasPasswordChanges
    },
    isEditingSelf () {
      return this.loggedInUser?.guid === this.userGuid
    },
    initiatorUserGuid () {
      return this.loggedInUser?.guid
    },
    tabs () {
      const tabs = [
        { key: 0, name: 'User Info', baseName: null, icon: null },
        { key: 1, name: 'Permissions', baseName: 'Permissions', icon: 'BriefCase' }
      ]
      if (this.isEditingSelf) {
        tabs.push({ key: 2, name: 'Change Password', baseName: 'Change Password', icon: 'BriefCase' })
      }
      return tabs
    }
  },

  watch: {
    value (val) {
      this.visible = val
    },
    initialTab (val) {
      this.tab = val
    },
    userGuid () {
      if (this.userGuid) {
        this.getUser()
        this.getRoleEnvironments()
      }
    },
    toggleDiscardChangesModalVisible () { this.resetAppTimeout() }
  },
  methods: {
    ...mapMutations(['setSuccessToast', 'setErrorToastType', 'setUser', 'resetAppTimeout']),
    handleUserInfoChange (userInfoChanges) {
      if (!isEmpty(userInfoChanges)) {
        this.hasUserInfoChanges = true
      } else {
        this.hasUserInfoChanges = false
      }
      this.resetAppTimeout()
    },
    handlePasswordChange (hasChanges) {
      this.hasPasswordChanges = hasChanges
    },
    toggleDiscardChangesModal () {
      this.toggleDiscardChangesModalVisible = !this.toggleDiscardChangesModalVisible
    },
    handleConfirmDiscardChanges () {
      this.tab = 0
      this.$emit('input', false)
      this.getUser()
      this.passwordKey++
    },
    onClose () {
      if (this.hasChanges) {
        this.toggleDiscardChangesModal()
      } else {
        this.tab = 0
        this.$emit('input', false)
      }
    },
    async getUser () {
      await this.$apollo.query({
        query: user,
        fetchPolicy: 'no-cache',
        variables: {
          guid: this.userGuid
        }
      }).then(({ data: { user } }) => {
        if (user) {
          this.user = user
          this.user.selectedOrgs = JSON.parse(user.selectedOrgsFormatted)
          this.user.guid = this.userGuid
          this.profilePictureUrl = user.profilePictureUrl
          this.lastLoginAt = user.lastLoginAt
        }
      })
    },
    getRoleEnvironments () {
      this.$apollo.query({
        query: rolesPermissions,
        variables: {
          companyGuid: this.companyGuid
        },
        fetchPolicy: 'no-cache'
      }).then(({ data }) => {
        const { nodes } = data.roles
        const envMap = {}
        nodes.map(node => {
          const flatPermissions = JSON.parse(node.flatPermissions)
          const { permission_setting_staging_enabled, permission_setting_production_enabled } = flatPermissions
          if (permission_setting_staging_enabled) {
            if (permission_setting_production_enabled) {
              envMap[node.name] = ['Production', 'Staging']
            } else {
              envMap[node.name] = ['Staging']
            }
          } else if (permission_setting_production_enabled) {
            envMap[node.name] = ['Production']
          }
        })
        this.roleEnvironments = envMap
      }).catch((err) => {
        this.setErrorToastType({ description: `There was an error with the request: ${err}` })
      })
    },
    async updateProfilePicture (image, filename) {
      if (!image || !filename) {
        this.setErrorToastType({ description: 'No image available to upload' })
        return
      }

      const { data } = await this.$apollo.mutate({
        mutation: updateUserProfilePicture,
        fetchPolicy: 'no-cache',
        variables: {
          guid: this.userGuid,
          image: image,
          filename: filename
        }
      })

      const { updateUserProfilePicture: { response, error } } = data
      if (response) {
        // this fetch ensures the photo is updated in the UI when the modal closes.
        const streamUrl = response
        await fetch(streamUrl).then(res => res.blob()).then(blob => {
          this.profilePictureUrl = URL.createObjectURL(blob)
        })

        this.setSuccessToast({ description: 'Profile picture has been saved' })
      } else if (error) {
        this.setErrorToastType({ description: `Failed to update profile picture: ${error}` })
      }

      if (this.userGuid === this.loggedInUser.guid && response) {
        this.setUser({ ...this.loggedInUser, profilePictureUrl: response })
      }

      this.getUser() // get updated information
    },
    handleUserInfoSave () {
      this.$emit('change')
      this.$emit('input', false)
      if (!this.isEditingSelf) {
        this.getUser()
      }
    },
    handlePasswordSave () {
      this.$emit('input', false)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui';
</style>
